import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React__default__default from 'react';
import PropTypes__default from 'prop-types';
import styles from './input.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import ReactUtils from '../../packages/react-utils/react-utils.js';
import PropsUtils from '../../packages/props-utils/props-utils.js';
import { InputInfoChildren, InputInfo } from '../_atoms/input-info/input-info.js';
import { InputField } from '../_atoms/input-field/input-field.js';
import { InlineLabel } from '../_atoms/inline-label/inline-label.js';
import { inputLabelValidator } from '../../prop-types-validators/input-label-validator.js';
import { iconValidator } from './icon-validator.js';
import { ALLOWED_INPUT_TYPES } from './types.js';
import { Injecss } from '../../packages/injecss/injecss.js';

var bem = HoneyBem.init('input', styles);
/**
 * Enhances Icon or Button with Input class names depending on the component type.
 * @param icon Icon or Button component
 * @returns Enhanced Icon or component for Input component
 */
function handleIcon(icon) {
  if (ReactUtils.isOfType(icon, 'Button')) {
    return /*#__PURE__*/React__default__default.cloneElement(icon, {
      display: 'square',
      link: true,
      size: 'sm',
      extraClasses: PropsUtils.handleExtraClasses(icon, bem.e('icon'))
    });
  }
  if (ReactUtils.isOfType(icon, 'Icon')) {
    return /*#__PURE__*/React__default__default.cloneElement(icon, {
      extraClasses: PropsUtils.handleExtraClasses(icon, bem.e('icon', {
        'click-through': true
      })),
      size: 4
    });
  }
  return null;
}
var Input = function (_a) {
  var extraClasses = _a.extraClasses,
    label = _a.label,
    id = _a.id,
    disabled = _a.disabled,
    valid = _a.valid,
    loading = _a.loading,
    _b = _a.type,
    type = _b === void 0 ? 'text' : _b,
    _c = _a.info,
    info = _c === void 0 ? '' : _c,
    _d = _a.infoError,
    infoError = _d === void 0 ? '' : _d,
    iconLeft = _a.iconLeft,
    iconRight = _a.iconRight,
    inlineLabelLeft = _a.inlineLabelLeft,
    inlineLabelRight = _a.inlineLabelRight,
    ariaDescribedByProp = _a["aria-describedby"],
    innerRef = _a.innerRef,
    hasInlineLabelAddon = _a.hasInlineLabelAddon,
    rest = __rest(_a, ["extraClasses", "label", "id", "disabled", "valid", "loading", "type", "info", "infoError", "iconLeft", "iconRight", "inlineLabelLeft", "inlineLabelRight", 'aria-describedby', "innerRef", "hasInlineLabelAddon"]);
  var classNames = bem.b({
    disabled: disabled,
    'has-icon-right': iconRight,
    error: !loading && valid === false,
    valid: !loading && valid === true,
    loading: loading
  }, extraClasses);
  var busy = loading && {
    busy: true
  };
  /**
   * Creates prioritized description id value by pushing all possible descriptions in order;
   * 1st: the left inline label (if present);
   * 2nd: the right inline label (if present);
   * 3rd: the inputInfo id (if `info` is present));
   */
  var describedByIds = inlineLabelLeft && !hasInlineLabelAddon ? ["inline-label-left-".concat(id)] : [''];
  if (inlineLabelRight) describedByIds.push("inline-label-right-".concat(id));
  if (info) describedByIds.push("info-".concat(id));
  return /*#__PURE__*/React__default__default.createElement("div", {
    className: classNames
  }, label && PropsUtils.handleLabel(label, id, bem), /*#__PURE__*/React__default__default.createElement("div", {
    className: bem.e('container')
  }, inlineLabelLeft && /*#__PURE__*/React__default__default.createElement(InlineLabel, {
    id: "left-".concat(id),
    className: bem.e('inline-label')
  }, inlineLabelLeft), iconLeft && handleIcon(iconLeft), /*#__PURE__*/React__default__default.createElement(InputField, __assign({
    ref: innerRef,
    id: id,
    className: bem.e('field'),
    type: type,
    disabled: disabled,
    "aria-errormessage": "infoError-".concat(id),
    valid: valid,
    "aria-describedby": PropsUtils.handleAriaDescribedBy(describedByIds.join(' '), ariaDescribedByProp)
  }, busy, rest)), /*#__PURE__*/React__default__default.createElement("span", {
    className: bem.e('feedback-icon'),
    "aria-hidden": "true"
  }), iconRight && handleIcon(iconRight), inlineLabelRight && /*#__PURE__*/React__default__default.createElement(InlineLabel, {
    id: "right-".concat(id),
    className: bem.e('inline-label')
  }, inlineLabelRight)), /*#__PURE__*/React__default__default.createElement(InputInfo, {
    id: id,
    error: true,
    className: bem.e('info')
  }, valid === false ? infoError : ''), info && /*#__PURE__*/React__default__default.createElement(InputInfo, {
    id: id,
    className: bem.e('info')
  }, info));
};
Input.displayName = 'Input';
Input.defaultProps = {
  extraClasses: undefined,
  children: undefined,
  label: undefined,
  disabled: false,
  valid: undefined,
  loading: false,
  type: 'text',
  info: undefined,
  infoError: undefined,
  iconLeft: undefined,
  iconRight: undefined,
  inlineLabelLeft: undefined,
  inlineLabelRight: undefined,
  innerRef: undefined,
  hasInlineLabelAddon: false
};
Input.propTypes = {
  extraClasses: PropTypes__default.string,
  label: inputLabelValidator(),
  id: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.number]).isRequired,
  disabled: PropTypes__default.bool,
  valid: PropTypes__default.bool,
  loading: PropTypes__default.bool,
  type: PropTypes__default.oneOf(ALLOWED_INPUT_TYPES),
  info: InputInfoChildren,
  infoError: InputInfoChildren,
  iconLeft: iconValidator(),
  iconRight: iconValidator(),
  inlineLabelLeft: PropTypes__default.node,
  inlineLabelRight: PropTypes__default.node,
  // @todo figure out how to make TypeScript agreed with this:
  // PropTypes.shape({
  //  current: PropTypes.instanceOf(typeof HTMLInputElement === 'undefined'
  //   ? () => null
  //   : HTMLInputElement)
  // }),
  innerRef: PropTypes__default.oneOfType([PropTypes__default.func, PropTypes__default.shape({
    current: PropTypes__default.object
  })]),
  hasInlineLabelAddon: PropTypes__default.bool
};
var Input$1 = Injecss(Input, styles);

export { Input, Input$1 as default };
